<template>
    <div>
        <rkm-modal
            extra-large
            persistent
            ref="RkmModal"
            title="Reagendamento"
        >
            <rkm-form
                ref="RkmForm"
                show-legend
            >
                <rkm-area
                    no-margin-top
                    title="Agendamentos"
                    type="fieldset"
                >
                    <rkm-row>
                        <rkm-col>
                            <rkm-table
                                :fields="fields.agendamento"
                                :items="agendamentos"
                                @filter-changed="handleAgendamentoFilterChanged"
                                @filtered="agendamentosFiltered = $event"
                            >
                                <template v-slot:head(checkbox)>
                                    <rkm-form-checkbox
                                        :indeterminate="indeterminate"
                                        :value="true"
                                        @change="checkUncheckAll"
                                        v-model="checkedAll"
                                    />
                                </template>

                                <template v-slot:cell(checkbox)="{item}">
                                    <rkm-form-checkbox
                                        :value="true"
                                        @change="handleCheckboxChange(item.id)"
                                        v-model="item.selecionado"
                                    />
                                </template>
                            </rkm-table>
                        </rkm-col>
                    </rkm-row>
                </rkm-area>

                <rkm-area
                    title="Jornadas"
                    type="fieldset"
                >
                    <rkm-row>
                        <rkm-col>
                            <rkm-table
                                :fields="fields.jornada"
                                :items="jornadas"
                                @filtered="data.model.destino.jornadaId = null"
                            >
                                <template v-slot:cell(radio)="{item}">
                                    <rkm-form-radio
                                        :disabled="item.desabilitada"
                                        :value="item.id"
                                        @click="handleJornadaClick"
                                        name="jornada"
                                        v-model="data.model.destino.jornadaId"
                                    />
                                </template>

                                <template v-slot:cell(quantidadeConsulta)="{item}">
                                    {{item.quantidadeConsultaAgendada}} / {{item.quantidadeConsultaTotal}}
                                </template>

                                <template v-slot:cell(quantidadeRetorno)="{item}">
                                    {{item.quantidadeRetornoAgendado}} / {{item.quantidadeRetornoTotal}}
                                </template>
                            </rkm-table>
                        </rkm-col>
                    </rkm-row>
                </rkm-area>

                <rkm-row v-if="isSupervisor">
                    <rkm-col>
                        <rkm-form-textarea
                            :validations="validations.motivoAgendamentoExcedente"
                            label="Motivo"
                            rows="4"
                            v-model="data.model.destino.motivoAgendamentoExcedente"
                        />
                    </rkm-col>
                </rkm-row>
            </rkm-form>

            <template v-slot:footer>
                <rkm-button @click="close">
                    Cancelar
                </rkm-button>

                <rkm-button
                    :disabled="quantidadeAgendamentosSelecionados === 0 || !data.model.destino.jornadaId"
                    @click="reschedule"
                    color="primary"
                >
                    Salvar
                </rkm-button>
            </template>
        </rkm-modal>

        <ModalSupervisor
            @authorized="isSupervisor = true"
            @canceled="handleModalSupervisorCanceled"
            ref="ModalSupervisor"
        />
    </div>
</template>

<script>
// Components.
import ModalSupervisor from '@/components/ambulatorio/components/supervisor/Modal'

// Enums.
import agendamentoTipo from '@/enums/ambulatorio/agendamentoTipo'

// Mixins.
import {error, repository, stringCase} from '@/mixins'

// Others.
import moment from 'moment'

// Vuex.
import {mapActions} from 'vuex'

export default {
    components: {ModalSupervisor},
    computed: {
        quantidadeAgendamentos() {
            return this.agendamentosFiltered.length
        },
        quantidadeAgendamentosSelecionados() {
            return this.agendamentos
                .filter(agendamento => this.agendamentosFiltered.find(agendamentoFiltered => agendamentoFiltered.id === agendamento.id))
                .filter(agendamento => agendamento.selecionado === true).length
        },
        jornadas() {
            const agendamentosSelecionados = this.agendamentos
                .filter(agendamento => this.agendamentosFiltered.find(agendamentoFiltered => agendamentoFiltered.id === agendamento.id))
                .filter(agendamento => agendamento.selecionado === true)

            return this.data.model.destino.jornadas && this.data.model.destino.jornadas.map(jornada => {
                const response = {...jornada, desabilitada: true}

                const hasAgendamentoConsulta = !!agendamentosSelecionados.find(agendamento => agendamento.tipoId === this.enums.agendamentoTipo.CONSULTA)
                const hasAgendamentoRetorno = !!agendamentosSelecionados.find(agendamento => agendamento.tipoId === this.enums.agendamentoTipo.RETORNO)

                if (hasAgendamentoConsulta && hasAgendamentoRetorno) {
                    if (jornada.quantidadeConsultaTotal > 0 && jornada.quantidadeRetornoTotal > 0) {
                        response.desabilitada = false
                    }
                }
                else if (hasAgendamentoConsulta && !hasAgendamentoRetorno) {
                    if (jornada.quantidadeConsultaTotal > 0) {
                        response.desabilitada = false
                    }
                }
                else if (!hasAgendamentoConsulta && hasAgendamentoRetorno) {
                    if (jornada.quantidadeRetornoTotal > 0) {
                        response.desabilitada = false
                    }
                }

                return response
            })
        },
        validations() {
            return {
                motivoAgendamentoExcedente: {
                    required: true,
                    max: 500,
                },
            }
        },
    },
    created() {
        this.repositories = {
            gerenciamentoMedico: this.getConectaSusRepository('ambulatorio.gerenciamentoMedico'),
        }
    },
    data: () => ({
        agendamentos: [],
        agendamentosFiltered: [],
        checkedAll: false,
        date: null,
        enums: {agendamentoTipo},
        fields: {
            agendamento: [
                {key: 'checkbox', label: '#', class: 'col-min text-center'},
                {key: 'horario', label: 'Horário'},
                {key: 'pacienteNome', label: 'Nome'},
                {key: 'pacienteDataNascimento', label: 'Data de nascimento'},
                {key: 'pacienteProntuario', label: 'Prontuário'},
                {key: 'tipoNome', label: 'Tipo'},
                {key: 'pacienteTelefone', label: 'Telefone'},
                {key: 'usuarioAgendadorNome', label: 'Agendador'},
            ],
            jornada: [
                {key: 'radio', label: '#', class: 'col-min text-center'},
                {key: 'data', label: 'Data'},
                {key: 'diaSemana', label: 'Dia da semana'},
                {key: 'inicio', label: 'Início'},
                {key: 'fim', label: 'Fim'},
                {key: 'quantidadeConsulta', label: 'Quantidade de consultas'},
                {key: 'quantidadeRetorno', label: 'Quantidade de retornos'},
            ],
        },
        indeterminate: false,
        isSupervisor: false,
        others: {moment},
    }),
    methods: {
        ...mapActions(['setLoading', 'unsetLoading']),
        open(date) {
            this.reset()

            this.date = date

            this.$refs.RkmModal.show()

            this.load()
        },
        load() {
            this.setLoading()

            Promise.all([
                this.getAgendamentos(this.date),
                this.getJornadas(),
            ]).then(([responseAgendamentos, responseJornadas]) => {
                this.agendamentos = Object.assign(responseAgendamentos.data.agendamentos.filter(a => !a.isFromRegulacao).map(agendamento => ({...agendamento, selecionado: false})))
                this.data.model.destino.jornadas = Object.assign(responseJornadas.data.jornadas)
            })
            .catch(this.handleError)
            .finally(() => this.unsetLoading())
        },
        handleAgendamentoFilterChanged() {
            this.checkedAll = false
            this.indeterminate = false
            this.agendamentos.map(agendamento => agendamento.selecionado = false)
        },
        checkUncheckAll(value) {
            this.checkedAll = value
            this.indeterminate = false
            this.data.model.destino.jornadaId = null

            this.agendamentos
                .filter(agendamento => this.agendamentosFiltered.find(agendamentoFiltered => agendamentoFiltered.id === agendamento.id))
                .map(agendamento => agendamento.selecionado = value)
        },
        handleCheckboxChange() {
            this.checkedAll = false
            this.indeterminate = false
            this.data.model.destino.jornadaId = null

            if (this.quantidadeAgendamentosSelecionados > 0) {
                if (this.quantidadeAgendamentosSelecionados === this.quantidadeAgendamentos) {
                    this.checkedAll = true
                } else  {
                    this.indeterminate = true
                }
            }
        },
        handleJornadaClick(event) {
            this.isSupervisor = false

            const quantidadeConsultasSelecionadas = this.agendamentos
                .filter(agendamento => this.agendamentosFiltered.find(agendamentoFiltered => agendamentoFiltered.id === agendamento.id))
                .filter(agendamento => agendamento.selecionado === true)
                .filter(agendamento => agendamento.tipoId === this.enums.agendamentoTipo.CONSULTA).length

            const quantidadeRetornosSelecionados = this.agendamentos
                .filter(agendamento => this.agendamentosFiltered.find(agendamentoFiltered => agendamentoFiltered.id === agendamento.id))
                .filter(agendamento => agendamento.selecionado === true)
                .filter(agendamento => agendamento.tipoId === this.enums.agendamentoTipo.CONSULTA).length

            const quantidadeMaximoAtingida = this.jornadas
                .filter(jornada =>
                    jornada.quantidadeConsultaTotal - jornada.quantidadeConsultaAgendada < quantidadeConsultasSelecionadas ||
                    jornada.quantidadeRetornoTotal - jornada.quantidadeRetornoAgendado < quantidadeRetornosSelecionados
                )
                .find(jornada => jornada.id == event.target.value)

            if (quantidadeMaximoAtingida) {
                this.$refs.ModalSupervisor.open()
            }
        },
        handleModalSupervisorCanceled() {
            this.isSupervisor = false
            this.data.model.destino.jornadaId = null
            this.data.model.destino.motivoAgendamentoExcedente = null
        },
        async getAgendamentos(data) {
            const request = {
                origem: 'origem',
                listar: 'agendamentos',
                usuarioId: this.usuarioId,
                infraestruturaId: this.data.model.origem.infraestruturaId,
                especialidadeId: this.data.model.origem.especialidadeId,
                data: this.others.moment(data).format('DD/MM/YYYY'),
            }

            return this.repositories.gerenciamentoMedico.list(this.camelObjectKeysToSnakeRecursive(request))
        },
        async getJornadas() {
            const request = {
                origem: 'destino',
                listar: 'jornadas',
                usuarioId: this.data.model.destino.usuarioId || this.usuarioId,
                infraestruturaId: this.data.model.destino.infraestruturaId || this.data.model.origem.infraestruturaId,
                especialidadeId: this.data.model.destino.especialidadeId || this.data.model.origem.especialidadeId,
            }

            return this.repositories.gerenciamentoMedico.list(this.camelObjectKeysToSnakeRecursive(request))
        },
        reschedule() {
            this.$refs.RkmForm.validate()
                .then(valid => {
                    if (valid === false) return

                    const agendamentos = this.agendamentos
                        .filter(agendamento => this.agendamentosFiltered.find(agendamentoFiltered => agendamentoFiltered.id === agendamento.id))
                        .filter(agendamento => agendamento.selecionado === true)

                    const request = {
                        agendamentos: agendamentos.map(agendamento => (agendamento.id)),
                        jornadaId: this.data.model.destino.jornadaId,
                    }

                    if (this.isSupervisor) {
                        request.motivoAgendamentoExcedente = this.data.model.destino.motivoAgendamentoExcedente
                    }

                    this.setLoading()

                    this.repositories.gerenciamentoMedico.reschedule(this.camelObjectKeysToSnakeRecursive(request))
                        .then(response => {
                            let warning = ''

                            if (response.data.agendamentosNaoReagendados.statusNaoPermiteAlteracao.length > 0) {
                                let message = ''

                                response.data.agendamentosNaoReagendados.statusNaoPermiteAlteracao.forEach(agendamentoId => {
                                    const agendamento = agendamentos.find(agendamento => agendamento.id === agendamentoId)

                                    message = message + `<li>
                                        Agendamento de ${agendamento.tipoNome} às ${agendamento.horario} horas, agendado por ${agendamento.usuarioAgendadorNome}
                                        para ${agendamento.pacienteNome}, nascido em ${agendamento.pacienteDataNascimento}.
                                    </li>`
                                })

                                warning = warning + `
                                    Agendamento(s) que não permite(m) alteração:
                                    <ul>${message}</ul>
                                `
                            }

                            if (response.data.agendamentosNaoReagendados.incompativelComEspecialidade.length > 0) {
                                let message = ''

                                response.data.agendamentosNaoReagendados.incompativelComEspecialidade.forEach(agendamentoId => {
                                    const agendamento = agendamentos.find(agendamento => agendamento.id === agendamentoId)

                                    message = message + `<li>
                                        Agendamento de ${agendamento.tipoNome} às ${agendamento.horario} horas, agendado por ${agendamento.usuarioAgendadorNome}
                                        para ${agendamento.pacienteNome}, nascido em ${agendamento.pacienteDataNascimento}.
                                    </li>`
                                })

                                warning = warning + `
                                    Agendamento(s) incompatível(eis) com a especialidade:
                                    <ul>${message}</ul>
                                `
                            }

                            if (warning) {
                                this.showWarningNotification(warning)
                            } else {
                                this.showSaveNotification()
                            }

                            this.reset()
                            this.load()
                        })
                        .catch(this.handleError)
                        .finally(() => this.unsetLoading())
                })
        },
        close() {
            this.data.form.destino.unidades = []
            this.data.form.destino.infraestruturas = []
            this.data.form.destino.ocupacoes = []
            this.data.form.destino.especialidades = []
            this.data.form.destino.usuarios = []
            this.data.model.destino.unidadeId = null
            this.data.model.destino.infraestruturaId = null
            this.data.model.destino.ocupacaoId = null
            this.data.model.destino.especialidadeId = null
            this.data.model.destino.usuarioId = null

            this.$refs.RkmModal.hide()

            this.$emit('closed')
        },
        reset() {
            this.agendamentos = []
            this.agendamentosFiltered = []
            this.checkedAll = false
            this.indeterminate = false
            this.isSupervisor = false
            this.data.model.destino.jornadas = []
            this.data.model.destino.jornadaId = null
            this.data.model.destino.motivoAgendamentoExcedente = null
        },
    },
    mixins: [error, repository, stringCase],
    props: {
        data: {type: Object, required: true},
        usuarioId: {type: Number, required: true},
    },
    watch: {
        agendamentos(value) {
            this.agendamentosFiltered = value
            this.checkedAll = false
            this.indeterminate = false
        },
    },
}
</script>